import React, { useMemo, useState } from 'react'
import VirtualList from 'rc-virtual-list'
import { List, Button, Input, Spin, Empty } from 'antd'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ArgumentOption, ArgumentOptionConfig } from '../types'
import backendRequest from '../../../../../../services/api/backendRequest'
import integrationRequest from '../../../../../../services/api/integrationRequest'

export interface ApiConfig {
  config: ArgumentOptionConfig
  companyId: string
  prevArgumentValue?: string
}

interface ArgumentOptionListProps {
  // Static options (for hardcoded lists, Redux selectors, etc.)
  options?: ArgumentOption[]

  // API request configuration (for context-aware requests)
  apiConfig?: ApiConfig

  // Common props
  height: number
  addArgument: (value: string) => void
}

const ArgumentOptionList: React.FC<ArgumentOptionListProps> = ({ options = [], apiConfig, height, addArgument }) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  // Helper function to generate URL from config
  const generateUrl = (urlConfig: any, companyId: string): string | null => {
    if (!urlConfig) return null

    if (typeof urlConfig === 'function') {
      // Function format: accountEndpoints.list
      return urlConfig(companyId)
    }

    if (typeof urlConfig === 'string') {
      // String template format: '/companies/{companyId}/accounts'
      return urlConfig.replace('{companyId}', companyId)
    }

    if (Array.isArray(urlConfig)) {
      // Legacy tuple format: [string, string | undefined] - for backward compatibility
      const [urlTemplate] = urlConfig
      return urlTemplate?.replace('{companyId}', companyId) || null
    }

    return null
  }

  // Generate query key - use config queryKey if available, fallback to URL-based key
  const getQueryKey = () => {
    if (!apiConfig) return ['argumentOptions', 'static']

    const { config, companyId } = apiConfig
    // Use queryKey from config if available
    if (config.request?.queryKey && companyId) {
      return config.request.queryKey({ companyId })
    }

    // Fallback to URL-based query key for backward compatibility
    const urlForKey = config.request?.url
    const requestType = config.request?.requestType || 'backend'

    if (typeof urlForKey === 'function') {
      return ['argumentOptions', requestType, 'function-url', companyId]
    }
    if (typeof urlForKey === 'string') {
      return ['argumentOptions', requestType, urlForKey, companyId]
    }
    if (Array.isArray(urlForKey)) {
      return ['argumentOptions', requestType, urlForKey[0], companyId]
    }

    return ['argumentOptions', requestType, 'unknown', companyId]
  }

  // Internal React Query for API requests
  const {
    data: apiData,
    isLoading,
    isError
  } = useQuery(
    apiConfig?.config.query && apiConfig.companyId
      ? apiConfig.config.query({ companyId: apiConfig.companyId })
      : {
          queryKey: getQueryKey(),
          queryFn: async () => {
            if (!apiConfig?.config.request) return []

            const { config, companyId } = apiConfig
            const url = generateUrl(config?.request?.url, companyId)

            if (!url) return []

            // Determine which request service to use
            const requestType = config?.request?.requestType || 'backend'
            const requestService = requestType === 'integration' ? integrationRequest : backendRequest

            // Build params only when needed and companyId exists
            const requestParams = requestType === 'integration' && companyId ? { companyId } : undefined

            // Return raw data from API
            const response = await requestService<any[]>({
              method: 'GET',
              url,
              params: requestParams
            })

            return response
          },
          select: (rawData: any) => {
            if (!apiConfig?.config.request) return []

            const { select, dataTransform } = apiConfig.config.request

            // First apply select function if provided (like a Redux selector)
            let processedData: any = rawData
            if (select && typeof select === 'function') {
              processedData = select(rawData)
            }

            // Then apply dataTransform to format each item
            if (!dataTransform) {
              return processedData
            }

            // Normalize processedData before mapping
            if (processedData == null) {
              return []
            }

            if (!Array.isArray(processedData)) {
              // If it's a single object, apply dataTransform and return as array
              return [dataTransform(processedData)]
            }

            // processedData is an array, proceed with normal mapping
            return processedData.map(dataTransform)
          },
          enabled:
            !!apiConfig?.config.request &&
            (!apiConfig.config.request?.requestType ||
              apiConfig.config.request.requestType !== 'integration' ||
              !!apiConfig.companyId),
          staleTime: 5 * 60 * 1000, // 5 minutes
          gcTime: 10 * 60 * 1000 // 10 minutes
        }
  )

  // Combine static options with API data
  const allOptions = useMemo(() => {
    const queryData = Array.isArray(apiData) ? apiData : []
    return [...options, ...queryData]
  }, [options, apiData])

  // Filter combined options based on search
  const filteredData = useMemo(() => {
    if (!search) return allOptions

    return allOptions.filter((item: ArgumentOption) => {
      return item.name.toLowerCase().includes(search.toLowerCase())
    })
  }, [search, allOptions])

  // Validation: ensure at least one data source is provided
  if (!options.length && !apiConfig) {
    console.warn('ArgumentOptionList: Either options or apiConfig must be provided')
  }

  return (
    <div>
      <Input
        placeholder={t('global:search')}
        onChange={e => setSearch(e.target.value)}
        style={{ margin: '1rem 0px' }}
        disabled={isLoading}
      />

      <List
        size="small"
        loading={
          isLoading
            ? {
                indicator: <Spin size="large" />,
                tip: t('global:loading')
              }
            : false
        }
        locale={{
          emptyText:
            filteredData.length === 0 || isError ? (
              <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            ) : undefined
        }}
      >
        {!isLoading && filteredData.length > 0 && !isError && (
          <VirtualList data={filteredData} height={height} itemHeight={41} itemKey="value">
            {item => (
              <List.Item
                className="virtual-list-item"
                key={item.value}
                actions={[
                  <Button
                    key={`add-${item.value}`}
                    size="small"
                    type="link"
                    onClick={() => addArgument(item.value.toString())}
                  >
                    {t('global:add')}
                  </Button>
                ]}
              >
                {item.name}
              </List.Item>
            )}
          </VirtualList>
        )}
      </List>
    </div>
  )
}

export default ArgumentOptionList
